import { useCallback, useEffect, useState } from 'react';
import { api, BladApi, token, type Sesja, type Zdrowie } from './api';
import { Footer } from './components/Footer';
import { Shell } from './components/Shell';
import { dopasuj, idz, START, useTrasa } from './router';
import { AssemblyOrderView } from './views/AssemblyOrderView';
import { AssemblyView } from './views/AssemblyView';
import { CustomersView } from './views/CustomersView';
import { DeviceView } from './views/DeviceView';
import { DevicesView } from './views/DevicesView';
import { LoginView } from './views/LoginView';
import { NewAssemblyView } from './views/NewAssemblyView';
import { NewDeviceView } from './views/NewDeviceView';
import { NewTicketView } from './views/NewTicketView';
import { TicketView } from './views/TicketView';
import { TicketsView } from './views/TicketsView';
import { PanelView } from './views/PanelView';
import { SettingsView } from './views/SettingsView';

export function App() {
  const [sesja, setSesja] = useState<Sesja | null>(null);
  const [zdrowie, setZdrowie] = useState<Zdrowie | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const [odtwarzanie, setOdtwarzanie] = useState(true);
  const { sciezka, parametry } = useTrasa();

  // Odtworzenie sesji po odświeżeniu strony: token leży w przeglądarce,
  // ale o jego ważności i uprawnieniach decyduje serwer.
  useEffect(() => {
    if (!token.odczytaj()) {
      setOdtwarzanie(false);
      return;
    }

    api
      .ja()
      .then(({ permissions, ...user }) => setSesja({ user, permissions }))
      .catch(() => token.usun())
      .finally(() => setOdtwarzanie(false));
  }, []);

  useEffect(() => {
    api
      .zdrowie()
      .then(setZdrowie)
      .catch(() => setZdrowie(null));
  }, []);

  const zaloguj = useCallback(async (email: string, haslo: string) => {
    setBlad(null);
    try {
      const wynik = await api.zaloguj(email, haslo);
      token.zapisz(wynik.token);
      setSesja({ user: wynik.user, permissions: wynik.permissions });
    } catch (error: unknown) {
      setBlad(error instanceof BladApi ? error.message : 'Wystąpił nieoczekiwany błąd.');
    }
  }, []);

  const wyloguj = useCallback(() => {
    token.usun();
    setSesja(null);
    setBlad(null);
  }, []);

  if (odtwarzanie) {
    return (
      <main className="app-ekran flex min-h-screen items-center justify-center">
        <span className="t-small text-n-500">Wczytywanie…</span>
      </main>
    );
  }

  if (!sesja) {
    return (
      <div className="app-ekran flex min-h-screen flex-col">
        <div className="flex-1">
          <LoginView onZaloguj={zaloguj} blad={blad} />
        </div>
        <Footer zdrowie={zdrowie} />
      </div>
    );
  }

  const szukane = parametry.get('q') ?? '';

  return (
    // `app-ekran` znika przy druku — na papier idzie wyłącznie naklejka
    // (patrz styles/index.css i DeviceView).
    <div className="app-ekran">
      <Shell
        sesja={sesja}
        sciezka={sciezka}
        szukane={szukane}
        zdrowie={zdrowie}
        onWyloguj={wyloguj}
      >
        <Widok sesja={sesja} sciezka={sciezka} szukane={szukane} />
      </Shell>
    </div>
  );
}

/** Zamienia token z naklejki na kartę urządzenia i wchodzi na nią. */
function PrzekierowanieZeSkanu({ token }: { token: string }) {
  const [blad, setBlad] = useState<string | null>(null);

  useEffect(() => {
    api
      .urzadzeniePoQr(token)
      .then((karta) => idz(`/urzadzenia/${karta.urzadzenie.id}`))
      .catch((error: unknown) => {
        setBlad(error instanceof BladApi ? error.message : 'Nie rozpoznano kodu.');
      });
  }, [token]);

  if (!blad) return <p className="t-small text-n-500">Otwieram kartę…</p>;

  return (
    <>
      <p className="t-body mb-13">{blad}</p>
      <button
        type="button"
        onClick={() => idz(START)}
        className="border border-n-300 bg-surface px-13 text-n-700 hover:bg-n-50"
      >
        <span className="t-label">Wróć do urządzeń</span>
      </button>
    </>
  );
}

function Widok({
  sesja,
  sciezka,
  szukane,
}: {
  sesja: Sesja;
  sciezka: string;
  szukane: string;
}) {
  if (sciezka === '/konto') return <PanelView sesja={sesja} />;
  if (sciezka === '/ustawienia') return <SettingsView sesja={sesja} />;
  if (sciezka === '/kontrahenci') {
    return <CustomersView key={szukane} sesja={sesja} szukane={szukane} />;
  }
  if (sciezka === '/urzadzenia/nowe') return <NewDeviceView />;
  if (sciezka === '/montaze/nowe') return <NewAssemblyView />;
  if (sciezka === '/zlecenia/nowe') return <NewTicketView />;

  if (sciezka === '/zlecenia') {
    return <TicketsView key={szukane} sesja={sesja} szukane={szukane} />;
  }

  const idZlecenia = dopasuj(sciezka, '/zlecenia/:id');
  if (idZlecenia) return <TicketView id={idZlecenia} sesja={sesja} />;

  if (sciezka === '/montaze') {
    return <AssemblyView key={szukane} sesja={sesja} szukane={szukane} />;
  }

  const idMontazu = dopasuj(sciezka, '/montaze/:id');
  if (idMontazu) return <AssemblyOrderView id={idMontazu} sesja={sesja} />;

  // Wejście z naklejki: token → karta. Osobna trasa, bo adres z kodu QR musi
  // działać także wtedy, gdy ktoś otworzy go z zewnątrz (docs/adr/0004).
  const tokenZNaklejki = dopasuj(sciezka, '/skan/:token');
  if (tokenZNaklejki) return <PrzekierowanieZeSkanu token={tokenZNaklejki} />;

  const idUrzadzenia = dopasuj(sciezka, '/urzadzenia/:id');
  if (idUrzadzenia) return <DeviceView id={idUrzadzenia} sesja={sesja} />;

  if (sciezka === '/urzadzenia' || sciezka === '/') {
    return <DevicesView key={szukane} sesja={sesja} szukane={szukane} />;
  }

  return (
    <>
      <h1 className="t-h1 mb-13">Nie ma takiej strony</h1>
      <button
        type="button"
        onClick={() => idz(START)}
        className="border border-n-300 bg-surface px-13 text-n-700 hover:bg-n-50"
      >
        <span className="t-label">Wróć do urządzeń</span>
      </button>
    </>
  );
}
